import { CheckCircle2, Undo2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/input';
import { api, errorMessage } from '@/lib/api';
import { flag } from '@/lib/feedback';
import { toast } from '@/lib/toast';
import { newIdempotencyKey } from '@/lib/utils';
import { money } from '@/features/accounting/api';

export interface BulkItem { id: string; version: number; number: string | null; label: string; amount?: number | null; by?: string | null }
interface Result { done: number; failed: { id: string; number: string | null; error: string }[]; batch?: string | null }

/** A batch this large (or this much money) is confirmed with a tick before it goes. */
const BIG_COUNT = 20;
const BIG_AMOUNT = 100_000;

/**
 * Approve (or send back) several documents together: shows what is selected, needs a reason (kept on each document's
 * history with a batch number), confirms large batches, and lists every one that could not be done and why.
 */
export function BulkApproveDialog({ items, onClose, onDone, action = 'approve' }: { items: BulkItem[] | null; onClose(): void; onDone(): void; action?: 'approve' | 'send_back' }) {
  const back = action === 'send_back';
  const [sure, setSure] = useState(false);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  // Reset only when a different selection is opened (the list re-renders with new arrays).
  const key = items?.map((i) => i.id).join() ?? '';
  useEffect(() => { if (key) { setReason(''); setErr(null); setResult(null); setSure(false); } }, [key]);
  const total = (items ?? []).reduce((s, i) => s + (i.amount ?? 0), 0);
  const go = async () => {
    if (reason.trim().length < 3) return flag(setErr, back ? 'Give the reason for sending these back — the creators see it (at least 3 characters)' : 'Give the reason for approving these together (at least 3 characters)');
    if (big && !sure) return flag(setErr, `Tick the box to confirm you have checked these ${items!.length} vouchers`);
    setBusy(true);
    try {
      const r = await api<Result>(`/documents/bulk/${action}`, { method: 'POST', body: { ids: items!.map((i) => i.id), comment: reason.trim(), versions: Object.fromEntries(items!.map((i) => [i.id, i.version])) }, idempotencyKey: newIdempotencyKey() });
      setResult(r);
      if (r.done) toast.success(`${back ? 'Sent back' : 'Approved'} ${r.done} of ${items!.length}`, r.batch ? { description: `Batch ${r.batch} — shown together in the audit log` } : undefined);
      if (r.failed.length) toast.error(`${r.failed.length} could not be ${back ? 'sent back' : 'approved'} — see the list`);
      onDone();
    } catch (e) {
      flag(setErr, errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const big = (items?.length ?? 0) >= BIG_COUNT || total >= BIG_AMOUNT;
  const byId = new Map((items ?? []).map((i) => [i.id, i]));
  return (
    <Dialog
      open={!!items}
      onOpenChange={(o) => !o && !busy && onClose()}
      title={result ? (back ? 'Sent back — result' : 'Bulk approval result') : `${back ? 'Send back' : 'Approve'} ${items?.length ?? 0} together`}
      description={result ? (result.batch ? `Batch ${result.batch}` : undefined) : `Total AED ${money(total, false)}. Each one is still checked on its own (permissions, approval limits, closed months${back ? '' : ', your own vouchers'}); any that fail are listed, not skipped silently.`}
      size="md"
      footer={result ? <Button onClick={onClose}>Close</Button> : (
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant={back ? 'danger' : undefined} loading={busy} onMouseDown={(e) => e.preventDefault()} onClick={() => void go()}>{back ? <Undo2 /> : <CheckCircle2 />} {back ? 'Send back' : 'Approve'} {items?.length ?? 0}</Button>
        </>
      )}
    >
      {result ? (
        <div className="space-y-3 text-[13px]">
          <p className="flex items-center gap-2 font-medium text-success"><CheckCircle2 className="size-4" /> {result.done} {back ? 'sent back' : 'approved'}</p>
          {result.failed.length > 0 && (
            <div>
              <p className="flex items-center gap-2 font-medium text-danger"><XCircle className="size-4" /> {result.failed.length} not {back ? 'sent back' : 'approved'} — still pending</p>
              <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto rounded-lg border border-border p-2">
                {result.failed.map((f) => <li key={f.id}><b className="font-mono text-[12px]">{f.number ?? byId.get(f.id)?.label}</b> — {f.error}</li>)}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <>
          <ul className="max-h-56 divide-y divide-border overflow-y-auto rounded-lg border border-border text-[12.5px]">
            {(items ?? []).map((i) => (
              <li key={i.id} className="flex items-center gap-3 px-3 py-1.5">
                <span className="font-mono text-[12px]">{i.number ?? '—'}</span>
                <span className="min-w-0 flex-1 break-words text-fg-muted">{i.label}{i.by ? ` · by ${i.by}` : ''}</span>
                {i.amount != null && <span className="tabular-nums">{money(i.amount, false)}</span>}
              </li>
            ))}
          </ul>
          <label htmlFor="bulk-reason" className="mt-3 block text-[13px] font-medium">{back ? 'What needs correcting (the same reason goes on each — edit any one later)' : 'Reason for approving together'} <span className="text-danger">*</span></label>
          <Textarea id="bulk-reason" className="mt-1.5" rows={2} value={reason} invalid={!!err} onChange={(e) => { setReason(e.target.value); if (err && e.target.value.trim().length >= 3) setErr(null); }} placeholder="e.g. Month-end receipts checked against the bank statement" autoFocus />
          {!back && <p className="mt-2 rounded-md bg-warning-soft px-3 py-2 text-[12px] text-warning">Approval cannot be undone. Once approved, a voucher can only be changed or cancelled through a change request.</p>}
          {big && <label className="mt-2 flex items-start gap-2 text-[12.5px]"><input type="checkbox" className="mt-0.5" checked={sure} onChange={(e) => { setSure(e.target.checked); if (e.target.checked) setErr(null); }} /> I have checked these {items?.length} vouchers, AED {money(total, false)} in all.</label>}
          {err && <p className="mt-1 text-xs text-danger" role="alert">{err}</p>}
        </>
      )}
    </Dialog>
  );
}
